import React, { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../../shared/auth/AuthProvider";

const SETTINGS = [
  ["reviewsEnabled", "เปิดรับรีวิวคอร์ส"],
  ["homepageReviewsEnabled", "แสดงรีวิวในหน้าแรก"],
];
const DEFAULTS = { reviewsEnabled: false, homepageReviewsEnabled: false };

// Match the canonical scalar role used by Firestore Rules. The UI is not the
// security boundary; Rules recheck authorization on every individual update.
export default function CourseReviewSettings({ courseId }) {
  const { user, userDoc, loading } = useAuth();
  if (loading || !user?.uid || userDoc?.role !== "admin") return null;
  const savedCourseId = courseId && courseId !== "new" ? courseId : null;
  return <AdminReviewSettings key={`${user.uid}:${savedCourseId || "new"}`} courseId={savedCourseId} />;
}

function AdminReviewSettings({ courseId }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState({});
  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState({});

  useEffect(() => {
    if (!courseId) return;
    let active = true;
    setLoaded(false);
    setLoadError("");
    getDoc(doc(db, "courses", courseId)).then((snapshot) => {
      if (!active) return;
      if (!snapshot.exists()) {
        setLoadError("ไม่พบคอร์ส กรุณาตรวจสอบคอร์สก่อนตั้งค่ารีวิว");
        return;
      }
      const data = snapshot.data();
      setSettings({
        reviewsEnabled: data.reviewsEnabled === true,
        homepageReviewsEnabled: data.homepageReviewsEnabled === true,
      });
      setLoaded(true);
    }).catch(() => {
      if (active) setLoadError("โหลดการตั้งค่ารีวิวไม่สำเร็จ กรุณาลองอีกครั้ง");
    });
    return () => { active = false; };
  }, [courseId, attempt]);

  const saveSetting = async (field, nextValue) => {
    if (!courseId || !loaded || busy[field]) return;
    setBusy((previous) => ({ ...previous, [field]: true }));
    setErrors((previous) => ({ ...previous, [field]: "" }));
    setSaved((previous) => ({ ...previous, [field]: false }));
    try {
      // Never use the editor's general metadata payload, setDoc or an upsert.
      await updateDoc(doc(db, "courses", courseId), {
        [field]: nextValue,
        updatedAt: serverTimestamp(),
      });
      setSettings((previous) => ({ ...previous, [field]: nextValue }));
      setSaved((previous) => ({ ...previous, [field]: true }));
    } catch {
      setErrors((previous) => ({
        ...previous,
        [field]: "บันทึกไม่สำเร็จ ค่ายังไม่เปลี่ยน กรุณาตรวจสอบสิทธิ์หรือการเชื่อมต่อแล้วลองอีกครั้ง",
      }));
    } finally {
      setBusy((previous) => ({ ...previous, [field]: false }));
    }
  };

  return (
    <section className="border-top pt-3 mt-3" aria-labelledby="course-review-settings-heading">
      <h6 id="course-review-settings-heading">การตั้งค่ารีวิว</h6>
      <p className="small text-muted" id="course-review-settings-help">
        แต่ละสวิตช์บันทึกทันทีและตั้งค่าแยกกัน เมื่อระบบรีวิวเปิดใช้งานจึงจะมีผลต่อผู้เรียนและหน้าแรก
      </p>
      {!courseId && <p className="small text-muted">กรุณาบันทึกคอร์สใหม่ก่อนตั้งค่ารีวิว</p>}
      {courseId && !loaded && !loadError && <p role="status" className="small text-muted">กำลังโหลดการตั้งค่ารีวิว...</p>}
      {loadError && (
        <Alert variant="danger">
          {loadError}{" "}
          <Button type="button" size="sm" variant="outline-danger" onClick={() => setAttempt((previous) => previous + 1)}>
            ลองโหลดอีกครั้ง
          </Button>
        </Alert>
      )}
      {SETTINGS.map(([field, label]) => (
        <div className="mb-3" key={field}>
          <Form.Check
            type="switch"
            id={field}
            label={label}
            checked={settings[field]}
            disabled={!loaded || busy[field]}
            aria-describedby={`course-review-settings-help ${field}-status`}
            onChange={(event) => saveSetting(field, event.target.checked)}
          />
          <div id={`${field}-status`} role="status" className="small text-muted" aria-live="polite">
            {busy[field] ? "กำลังบันทึก..." : saved[field] ? "บันทึกแล้ว" : ""}
          </div>
          {errors[field] && <Alert variant="danger" className="small mt-2 mb-0">{errors[field]}</Alert>}
        </div>
      ))}
    </section>
  );
}
